import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "../api/client";

const TYPE_META: Record<string, { title: string; noun: string }> = {
  holiday:    { title: "New holiday estimate",       noun: "holiday" },
  hospital:   { title: "New hospital-stay estimate", noun: "hospital stay" },
  transition: { title: "New transition estimate",    noun: "transition" },
};

export function HolidayNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = (searchParams.get("type") ?? "holiday").toLowerCase();
  const meta = TYPE_META[type] ?? TYPE_META.holiday;
  const homes = useQuery({ queryKey: ["homes"], queryFn: api.homes });
  const funders = useQuery({ queryKey: ["funders"], queryFn: api.funders });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const isCommercial = !!(me.data?.roles?.approver || me.data?.roles?.admin);

  const [homeId, setHomeId] = useState<number | "">("");
  const [pwsId, setPwsId] = useState<number | "">("");
  const [pwsInitials, setPwsInitials] = useState("");
  const [funderId, setFunderId] = useState<number | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [staffCount, setStaffCount] = useState(1);

  const people = useQuery({
    queryKey: ["people", homeId],
    queryFn: () => api.peopleByHome(homeId === "" ? undefined : Number(homeId)),
    enabled: homeId !== "",
  });

  const selectedPws = useMemo(
    () => (people.data?.people ?? []).find((p: any) => p.id === Number(pwsId)),
    [people.data, pwsId],
  );

  const selectedFunder = useMemo(
    () => (funders.data?.funders ?? []).find((f: any) => f.id === Number(funderId)),
    [funders.data, funderId],
  );

  const create = useMutation({
    mutationFn: () => api.createEstimate({
      type,
      pws_id: pwsId === "" ? null : Number(pwsId),
      pws_initials: pwsInitials || selectedPws?.initials || "??",
      home_id: homeId === "" ? null : Number(homeId),
      funder_id: funderId === "" ? null : Number(funderId),
      start_date: startDate || null,
      end_date: endDate || null,
      staff_count: staffCount,
    }),
    onSuccess: (data) => navigate(`/holiday/${data.id}`),
  });

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-liaise-navy">{meta.title}</h1>
        <p className="text-slate-500 mt-1">Step 1 of 2. Set up the request, then add the day-by-day schedule.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
        <Row label="Home">
          <select className="input" value={homeId} onChange={(e) => { setHomeId(e.target.value === "" ? "" : Number(e.target.value)); setPwsId(""); }}>
            <option value="">Select a home...</option>
            {(homes.data?.homes ?? []).map((h: any) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
        </Row>

        <Row label="Person we support">
          <select
            className="input"
            disabled={homeId === ""}
            value={pwsId}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              setPwsId(v);
              if (v !== "") {
                const p = (people.data?.people ?? []).find((pp: any) => pp.id === v);
                if (p) {
                  setPwsInitials(p.initials ?? "");
                  if (p.primary_funder_id) setFunderId(p.primary_funder_id);
                }
              }
            }}
          >
            <option value="">Select a PWS...</option>
            {(people.data?.people ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.initials})</option>
            ))}
          </select>
          {homeId === "" && <div className="text-xs text-slate-400 mt-1">Pick a home first.</div>}
          {homeId !== "" && people.isFetched && (people.data?.people ?? []).length === 0 && (
            <div className="text-xs text-slate-400 mt-1">No people we support are recorded against this home yet. Type the initials manually below.</div>
          )}
        </Row>

        <Row label="PWS initials (override)">
          <input className="input" maxLength={20} value={pwsInitials} onChange={(e) => setPwsInitials(e.target.value)} placeholder="MM" />
        </Row>

        <Row label="Funder">
          <select className="input" value={funderId} onChange={(e) => setFunderId(e.target.value === "" ? "" : Number(e.target.value))}>
            <option value="">Select a funder...</option>
            {(funders.data?.funders ?? []).map((f: any) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {selectedFunder?.case_by_case && (
            <div className="mt-2 text-xs px-3 py-2 rounded bg-liaise-orange/15 text-orange-800">
              ⚠ Case by Case funder. Extra documentation needed.
              {selectedFunder.case_by_case_deadline ? ` Deadline ${formatDate(selectedFunder.case_by_case_deadline)}.` : ""}
              {selectedFunder.case_by_case_folder_url && (
                <> <a href={selectedFunder.case_by_case_folder_url} target="_blank" rel="noreferrer" className="underline">Open SharePoint folder</a>.</>
              )}
            </div>
          )}
        </Row>

        <div className="grid grid-cols-2 gap-4">
          <Row label="Start date">
            <input type="date" className="input" value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)} />
          </Row>
          <Row label="End date">
            <input type="date" className="input" value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)} />
          </Row>
        </div>
        {(() => {
          const invalid = !!(startDate && endDate && endDate < startDate);
          return invalid ? (
            <div className="px-4 py-3 rounded-xl bg-liaise-red/10 text-red-700 text-sm">
              ⚠ End date is before the start date. Fix the dates before continuing.
            </div>
          ) : null;
        })()}

        <Row label="No. of staff">
          <input type="number" min={1} className="input w-32" value={staffCount}
            onChange={(e) => setStaffCount(Math.max(1, Number(e.target.value)))} />
          <div className="text-xs text-slate-400 mt-1">Drives Staff Expenses only. Day, night and sleep-in costs are independent of headcount.</div>
        </Row>

        {selectedPws && <PwsContextCard pws={selectedPws} isCommercial={isCommercial} />}

        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          <button
            className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={create.isPending || !homeId || !startDate || !endDate || endDate < startDate}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating..." : "Continue to schedule →"}
          </button>
          {(!startDate || !endDate || endDate < startDate) && (
            <span className="text-xs text-slate-500">Set valid dates first.</span>
          )}
          {create.error && <span className="text-xs text-red-600">{(create.error as Error).message}</span>}
        </div>
      </div>

      <style>{`.input{display:block;width:100%;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;font-size:.875rem;background:white}`}</style>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function PwsContextCard({ pws }: { pws: any; isCommercial: boolean }) {
  // Commercial figures (Weekly fee / Annual contribution / EBITDA / Dashboard /
  // Partnership RAG) are not relevant to Holiday / Hospital / Transition pricing
  // and have historically caused issues when forwarded to funders, so we hide
  // them on this form for all roles. They remain visible on PwsProfile for
  // Approvers and Admins when they need them.
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-1">
      <div className="font-medium text-liaise-navy">{pws.first_name} {pws.last_name} ({pws.initials})</div>
      <div className="text-slate-500 text-xs">{pws.home_name} · {pws.region}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
        <div>Funder: <span className="text-slate-800">{pws.primary_funder_name ?? "-"}</span></div>
        <div>Acuity: <span className="text-slate-800">{pws.acuity ?? "-"}</span></div>
      </div>
    </div>
  );
}
